import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "multi-factor-authentication",
  courseId: "account-and-security",
  order: 2,
  title: "Multi-factor auth",
  summary:
    "Sign in with an authenticator app, device biometrics, or an emailed code, backed by backup codes and trusted devices. Required when your agency enforces it, available to anyone.",
  learn: [
    "Enroll an authenticator app, a passkey, or email codes",
    "Store backup codes and manage trusted devices",
    "Understand session limits under CJIS",
  ],
  minutes: 4,
  releasedAt: "2025-06-01T00:00:00.000Z",
  body: [
    {
      type: "paragraph",
      text: "If your agency enforces MFA, you are asked to set it up at your next login and offered the methods your admin allowed. You can also enroll on your own from **Settings**, under **Security**.",
    },
    { type: "heading", text: "Enroll a method" },
    {
      type: "steps",
      items: [
        "Open **Settings** and scroll to **Security**.",
        "Authenticator App: scan the QR code in your authenticator, then type the six-digit code.",
        "Email Verification: enter the code sent to your email.",
        "Device Biometrics: click **Add a device**, then **Register**, to create a passkey with Touch ID, Face ID, or a security key.",
        "Backup Codes: save the codes shown when you enroll. Reissue them here if you run out.",
        "Trusted Devices: click **Revoke** on any browser you do not recognize.",
      ],
    },
    {
      type: "paragraph",
      text: "An authenticator enrollment is not complete until the code is accepted. Device Biometrics is web only. Trusted Devices lists the browsers you chose to remember.",
    },
    {
      type: "callout",
      tone: "warning",
      title: "Save the backup codes",
      text: "A backup code is the only way in if you lose your phone and did not enroll a second method. Keep the codes somewhere you can reach without your phone, because they are shown once. If you enrolled only a passkey on your desktop, the iOS app can sign you in with a backup code only, so enroll an authenticator or email too.",
    },
    { type: "heading", text: "Sessions" },
    {
      type: "paragraph",
      text: "Sessions last at most 12 hours and expire after 30 minutes of inactivity. That is a CJIS requirement, not something you can change. A trusted device skips the second factor for the number of days your admin set, but still obeys the session limits.",
    },
    {
      type: "paragraph",
      text: "On iOS the same methods work except device biometrics, which is web only. Face ID on the phone is a convenience unlock, not a second factor.",
    },
  ],
  related: ["mfa-policy", "account-and-billing", "the-mobile-app"],
};

export default lesson;
